import { v4 as uuidv4 } from 'uuid';
import type { FileUIPart } from 'ai';
import type { GroupId, UserId, ViewModelId } from 'balus-common';
import { StoragePath } from 'balus-common';
import { StorageRefBuilder } from '@framework/firebase/storage';

// 1メッセージあたりに添付できるファイル（スプレッドシート・テキストファイル）の最大数（バックエンドの検証と揃える）
export const MAX_CHAT_FILE_ATTACHMENTS = 2;
// 画像とファイルを合わせた添付の最大数（クライアント側 UX の上限）
export const MAX_TOTAL_CHAT_ATTACHMENTS = 4;
// 1ファイルあたりのサイズ上限（storage.rules の 10MB と揃える）
export const MAX_CHAT_FILE_BYTES = 10 * 1024 * 1024;

/**
 * スプレッドシート形式の拡張子→contentTypeの対応表。ここに無い拡張子は全てテキスト候補として扱う
 * （json/yaml等も含めて拡張子では絞り込まない。実際にテキストとして読めるかはバックエンドが内容
 * そのもの＝UTF-8/Shift_JIS/UTF-16としての妥当性で判定し、読み込めなければチャット上に
 * 「読み込めません」と表示される）。種別判定とcontentType正規化を同じ表から導出することで、
 * 拡張子を追加する際に片方だけ更新して食い違う（storage.rulesの許可リストと不一致になる）事故を防ぐ。
 * storage.rules / バックエンドの mediaType allowlist と一致させ、ブラウザ申告 MIME には依存しない。
 */
const SPREADSHEET_CONTENT_TYPE_BY_EXTENSION = {
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.tsv': 'text/tab-separated-values',
  '.csv': 'text/csv',
} as const;

// ファイルの種別（バックエンドの展開経路・GCS保存先を分けるための区分）。
type ChatFileKind = 'spreadsheet' | 'text';

function findSpreadsheetExtension(filename: string): keyof typeof SPREADSHEET_CONTENT_TYPE_BY_EXTENSION | undefined {
  const lower = filename.toLowerCase();
  return (
    Object.keys(SPREADSHEET_CONTENT_TYPE_BY_EXTENSION) as (keyof typeof SPREADSHEET_CONTENT_TYPE_BY_EXTENSION)[]
  ).find((ext) => lower.endsWith(ext));
}

function chatFileKindOf(filename: string): ChatFileKind {
  return findSpreadsheetExtension(filename) === undefined ? 'text' : 'spreadsheet';
}

// 拡張子から contentType を正規化する（.md のみ text/markdown、スプレッドシート以外は既定で text/plain。
// json/yaml 等の内容そのものの解釈はバックエンドのテキスト展開に委ねる）。
function normalizeChatFileContentType(filename: string): string {
  const spreadsheetExt = findSpreadsheetExtension(filename);
  if (spreadsheetExt !== undefined) return SPREADSHEET_CONTENT_TYPE_BY_EXTENSION[spreadsheetExt];
  if (filename.toLowerCase().endsWith('.md')) return 'text/markdown';
  return 'text/plain';
}

/**
 * 送信前のチャット添付ファイル。画像と違い圧縮・プレビュー画像は持たず、
 * チップ表示のためのメタ情報と、送信時にアップロードする元 blob だけを保持する。
 */
export type PendingChatFileAttachment = {
  // クライアント側でチップ表示・削除を識別するための一時ID
  id: string;
  filename: string;
  mediaType: string;
  sizeBytes: number;
  blob: Blob;
  kind: ChatFileKind;
};

/**
 * File を送信前の添付に変換する（圧縮なし・拡張子から contentType を正規化）。
 */
export function toPendingChatFileAttachment(file: File): PendingChatFileAttachment {
  return {
    id: uuidv4(),
    filename: file.name,
    mediaType: normalizeChatFileContentType(file.name),
    sizeBytes: file.size,
    blob: file,
    kind: chatFileKindOf(file.name),
  };
}

/**
 * 添付ファイルを Firebase Storage にアップロードし、送信用の FileUIPart（参照パス）を返す。
 * url は data URL ではなく自アプリ内の参照パスにすることで、履歴・RTDB には参照だけが残る。
 * contentDisposition を attachment にして、履歴からの再取得時にダウンロードとして扱わせる。
 */
export async function uploadChatFileAttachment(
  attachment: PendingChatFileAttachment,
  params: { groupId: GroupId; viewModelId: ViewModelId; userId: UserId; threadId: string }
): Promise<FileUIPart> {
  const fileId = uuidv4();
  const path =
    attachment.kind === 'text'
      ? StoragePath.aiChatTextFilePath(params.groupId, params.viewModelId, params.userId, params.threadId, fileId)
      : StoragePath.aiChatFilePath(params.groupId, params.viewModelId, params.userId, params.threadId, fileId);
  const storageRef = StorageRefBuilder.ref(path);

  await storageRef.put(attachment.blob, {
    contentType: attachment.mediaType,
    contentDisposition: `attachment; filename*=utf-8''${encodeURIComponent(attachment.filename)}`,
    customMetadata: {
      originalFilename: attachment.filename,
    },
  });

  return {
    type: 'file',
    mediaType: attachment.mediaType,
    filename: attachment.filename,
    // 配信プロキシ・バックエンド検証が期待する先頭スラッシュ付きの参照パス
    url: `/${path}`,
  };
}
